// This device's private index of encrypted chats (29-saved-later.md, "The index of encrypted chats"):
// every text message it decrypts, per account in IndexedDB, searched on a message search's first page.
// It never leaves the device; switching it off or signing out deletes it. The walk through older
// history was a candidate to drop.
import { MatrixEventEvent, RoomEvent, type MatrixClient, type MatrixEvent, type Room } from 'matrix-js-sdk';
import { onSettings, setting } from '../core/settings';

interface Entry {
  id: string;
  room: string;
  sender: string;
  ts: number;
  text: string;
}

const nameOf = (userId: string) => `desyr_search_${userId}`;
const opened = new Map<string, Promise<IDBDatabase>>();
/** Dropped while signing out: a late decryption never opens it again (its deletion would wait on it). */
const dropped = new Set<string>();

function open(userId: string): Promise<IDBDatabase> {
  if (dropped.has(userId)) return Promise.reject(new Error('dropped'));
  let db = opened.get(userId);
  if (!db) {
    db = new Promise((resolve, reject) => {
      const r = indexedDB.open(nameOf(userId), 1);
      r.onupgradeneeded = () => r.result.createObjectStore('messages', { keyPath: 'id' }).createIndex('room', 'room');
      r.onsuccess = () => resolve(r.result);
      r.onerror = () => reject(r.error);
    });
    opened.set(userId, db);
  }
  return db;
}

async function store(userId: string, work: (s: IDBObjectStore) => void | IDBRequest): Promise<unknown> {
  const db = await open(userId);
  return new Promise((resolve, reject) => {
    const tx = db.transaction('messages', 'readwrite');
    const r = work(tx.objectStore('messages'));
    tx.oncomplete = () => resolve(r?.result);
    tx.onerror = () => reject(tx.error);
  });
}

/** Deletes the index on this device (switched off, signed out). */
export async function dropIndex(userId: string, signingOut = false): Promise<void> {
  if (signingOut) dropped.add(userId);
  const db = opened.get(userId);
  opened.delete(userId);
  (await db?.catch(() => undefined))?.close();
  await new Promise((r) => {
    const req = indexedDB.deleteDatabase(nameOf(userId));
    req.onsuccess = req.onerror = req.onblocked = r;
  });
}

/** Keeps the index while the switch is on: decrypted text messages in, deleted ones and left chats out. */
export function startIndex(client: MatrixClient): () => void {
  const me = client.getUserId()!;
  const on = () => setting('encrypted-search') !== false;
  const add = (ev: MatrixEvent) => {
    if (!on() || !ev.isEncrypted() || ev.isDecryptionFailure() || ev.isRedacted() || ev.getType() !== 'm.room.message') return;
    const c = ev.getContent();
    const relation = c['m.relates_to'];
    const edit = relation?.rel_type === 'm.replace';
    const text = edit ? c['m.new_content']?.body : c.body;
    if (typeof text !== 'string' || !text.trim()) return;
    const entry: Entry = { id: edit ? String(relation.event_id) : ev.getId()!, room: ev.getRoomId()!, sender: ev.getSender()!, ts: ev.getTs(), text: text.toLowerCase() };
    void store(me, (s) => s.put(entry)).catch(() => {});
  };
  const redacted = (ev: MatrixEvent) => {
    const id = ev.event.redacts ?? ev.getContent().redacts;
    if (on() && id) void store(me, (s) => s.delete(id)).catch(() => {});
  };
  const membership = (room: Room, membership: string) => {
    if (!on() || membership === 'join') return;
    void store(me, (s) => {
      const keys = s.index('room').getAllKeys(room.roomId);
      keys.onsuccess = () => keys.result.forEach((k) => s.delete(k));
    }).catch(() => {});
  };
  let wasOn = on();
  const offSettings = onSettings(() => {
    if (wasOn && !on()) void dropIndex(me);
    wasOn = on();
  });
  client.on(MatrixEventEvent.Decrypted, add);
  client.on(RoomEvent.Redaction, redacted);
  client.on(RoomEvent.MyMembership, membership);
  return () => {
    offSettings();
    client.off(MatrixEventEvent.Decrypted, add);
    client.off(RoomEvent.Redaction, redacted);
    client.off(RoomEvent.MyMembership, membership);
  };
}

/** Entries holding every word (any case), in the chats and from the senders asked for; newest first, at most 100. */
export async function searchIndex(userId: string, term: string, rooms?: string[], senders?: string[]): Promise<Entry[]> {
  if (setting('encrypted-search') === false) return [];
  const words = term.toLowerCase().split(/\s+/).filter(Boolean);
  const all = (await store(userId, (s) => s.getAll())) as Entry[];
  return all
    .filter((e) => (!rooms || rooms.includes(e.room)) && (!senders || senders.includes(e.sender)) && words.every((w) => e.text.includes(w)))
    .sort((a, b) => b.ts - a.ts)
    .slice(0, 100);
}
